<template>
  <v-container>
    <template v-if="!loading">
      <v-row>
        <v-col cols="12" lg="6" md="12" offset-lg="3" class="mt-12">
          <h2 class="section-heading">
            {{ $t("message.common.subscription") }}
          </h2>
          <template v-if="isSubscriptionActive">
            <p>
              <span
                v-if="isFreeTrial"
                v-html="$t('message.payments.freePlanDescription')"
              >
              </span>
              <template v-else>
                <div class="mb-4">
                  {{ $t("message.payments.activeSubscriptionDescription") }}
                </div>

                <div>
                  {{
                    $t("message.companyProfile.settings.subscriptionName", {
                      name: planName
                    })
                  }}
                </div>
              </template>
            </p>
            <p v-if="!isFreeTrial">
              <template v-if="isSubscriptionPlanClient">
                {{
                  $t("message.companyProfile.settings.subscriptionExpiration", {
                    date: planExiresAt
                  })
                }}
              </template>
              <template v-else>
                {{
                  $t("message.payments.subscriptionExpiryDate", {
                    date: planExiresAt
                  })
                }}
              </template>
            </p>
            <p v-else>
              {{ $t("message.payments.freeTrialEndsIn", [planExiresAt]) }}
            </p>
          </template>
          <template v-else>
            <p v-if="isSubscriptionPlanClient">
              <template v-if="initialPaymentNotStarted">{{
                $t("message.payments.freeTrialEnded")
              }}</template>
              <template v-else>
                {{
                  $t("message.companyProfile.settings.subscriptionExpired", {
                    name: planName
                  })
                }}
              </template>
            </p>
            <p v-else>
              {{ $t("message.companyProfile.settings.expired") }}
            </p>
          </template>
        </v-col>
        <v-col
          v-if="
            isSubscriptionPlanClient &&
              !isFreeTrial &&
              !initialPaymentNotStarted
          "
          cols="12"
          lg="6"
          md="12"
          offset-lg="3"
        >
          <div class="buttons buttons--subscription">
            <v-btn target="_blank" color="primary" @click="goToStripe">
              {{ $t("message.companyProfile.settings.manageSubscription") }}
            </v-btn>
            <v-btn color="primary" outlined @click="pay">
              {{ $t("message.companyProfile.settings.pay") }}
            </v-btn>
          </div>
        </v-col>
        <v-col
          v-else-if="isSubscriptionPlanClient && initialPaymentNotStarted"
          cols="12"
          lg="6"
          md="12"
          offset-lg="3"
        >
          <div class="buttons buttons--subscription">
            <v-btn target="_blank" color="primary" @click="goToOrderDetails">
              {{ $t("message.payments.selectPlanButton") }}
            </v-btn>
          </div>
        </v-col>
        <v-col
          v-if="isSubscriptionPlanClient"
          cols="12"
          lg="6"
          md="12"
          offset-lg="3"
        >
          <v-form
            v-model="valid"
            :class="{ 'mt-5': $vuetify.breakpoint.smAndUp }"
          >
            <v-card outlined class="pa-5">
              <h2 class="mb-3">
                {{ $t("message.common.billingData") }}
              </h2>
              <v-text-field
                :key="form.gov_tax_number.id"
                v-model="form.gov_tax_number.value"
                v-validate="{ required: form.gov_tax_number.required }"
                :data-vv-name="$t(form.gov_tax_number.label)"
                :error-messages="errors.collect($t(form.gov_tax_number.label))"
                :label="
                  form.gov_tax_number.required
                    ? `${$t(form.gov_tax_number.label)} *`
                    : $t(form.gov_tax_number.label)
                "
                :required="form.gov_tax_number.required"
                :disabled="form.gov_tax_number.disabled || !editable"
                append-icon="icon-search"
                @click:append="handleTaxNumberSearch"
              ></v-text-field>
              <v-text-field
                v-for="item in [
                  form.legal_name,
                  form.city,
                  form.postal_code,
                  form.street
                ]"
                :key="item.id"
                v-model="item.value"
                v-validate="{ required: item.required }"
                :data-vv-name="$t(item.label)"
                :error-messages="errors.collect($t(item.label))"
                :label="item.required ? `${$t(item.label)} *` : $t(item.label)"
                :required="item.required"
                :disabled="item.disabled || !editable"
              ></v-text-field>
              <v-select
                v-model="form.country_code.value"
                :items="countries"
                :data-vv-name="$t(form.country_code.label)"
                :error-messages="errors.collect($t(form.country_code.label))"
                :label="
                  form.country_code.required
                    ? `${$t(form.country_code.label)} *`
                    : $t(form.country_code.label)
                "
                :required="form.country_code.required"
                :disabled="form.country_code.disabled || !editable"
                item-text="name"
                item-value="code"
              ></v-select>
            </v-card>
          </v-form>
          <div class="buttons">
            <ui-button
              v-if="!editable"
              color="primary"
              @click="handleEditClick"
              outlined
              icon="icon-edit"
              large
              class="my-4"
              width="140"
            >
              {{ $t("message.common.edit") }}
            </ui-button>
            <ui-button
              v-if="editable"
              color="primary"
              @click="handleCancelClick"
              outlined
              large
              class="my-4"
              width="140"
            >
              {{ $t("message.common.cancel") }}
            </ui-button>
            <ui-button
              v-if="editable"
              color="primary"
              @click="handleSubmitForm"
              :icon="!editable ? 'icon-edit' : undefined"
              large
              class="my-4"
              width="140"
            >
              {{ $t("message.common.save") }}
            </ui-button>
          </div>
        </v-col>
        <SectionLoader :status="loading" :opacity="1" />
      </v-row>
    </template>

    <ui-dialog :show="planChangeDialog" :closable="false" max-width="542px">
      <template #header>
        <h3>
          {{ $t("message.payments.planDialog.title") }}
        </h3>
      </template>
      <div class="content">
        {{ $t("message.payments.planDialog.description") }}
      </div>
      <template #actions>
        <div class="actions">
          <ui-button color="primary" @click="refreshPage">
            {{ $t("message.common.refresh") }}
          </ui-button>
        </div>
      </template>
    </ui-dialog>
  </v-container>
</template>

<script>
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import ErrorHandler from "@/helpers/handlers/error";

import usersService from "@/services/users";
import PaymentsService from "@/services/payments";

import { companyProfileMixin } from "@/helpers/mixins/company-profile";
import serviceHandler from "@/helpers/handlers/service";
import companyService from "@/services/company";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

export default {
  name: "UserSettingsConsentsAndRegulations",
  mixins: [companyProfileMixin],
  components: {
    SectionLoader
  },
  data() {
    return {
      planChangeDialog: false,
      consentLoading: false,
      isMarketingConsent: false,
      accountCode: "",
      editable: false,
      valid: true
    };
  },

  computed: {
    initialPaymentNotStarted() {
      return (
        this.$store.state.company.company.initial_payment_status !== "succeeded"
      );
    },
    isFreeTrial() {
      return this.$store.getters["auth/isFreeTrial"];
    },
    isSubscriptionActive() {
      return new Date(this.planExiresAt) > new Date();
    },

    isEnterpriseClient() {
      return false; // TODO: get from backend information if client is enterprise
    },
    isSubscriptionPlanClient() {
      return this.$store.getters["company/isSubscriptionPlanClient"];
    },

    stripeUrl() {
      return process.env.VUE_APP_STRIPE_MANAGE_PLAN_URL;
    },

    planName() {
      return this.$store.getters["auth/planName"];
    },

    planExiresAt() {
      return this.$store.getters["auth/planExpiresAt"];
    }
  },

  async created() {
    this.$store.commit("company/SET_LOADING", true);
    await this.fetchCountries();
    await this.setForm();
    this.$store.commit("company/SET_LOADING", false);
  },

  methods: {
    async handleSubmitForm() {
      this.handleSaveClick();
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.SUBSCRIPTION_SETTINGS_BILLING_DATA_UPDATED
      });
    },
    async handleTaxNumberSearch(e) {
      await this.onTaxNumberSearch(e);
      this.$gtm.trackCustomEvent({
        event:
          GTM_EVENT_NAME.SUBSCRIPTION_SETTINGS_BILLING_DATA_LOADED_AUTOMATICALLY_FOR_TAX_ID
      });
    },
    goToOrderDetails() {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.SUBSCRIPTION_SETTINGS_PLAN_SELECTION_STARTED
      });
      this.$router.push({ name: "order-details" });
    },
    refreshPage() {
      this.planChangeDialog = false;
      this.$router.go();
    },
    goToStripe() {
      this.$gtm.trackCustomEvent({
        event:
          GTM_EVENT_NAME.SUBSCRIPTION_SETTINGS_SUBSCRIPTION_MANAGEMENT_PANEL_OPENED
      });
      const stripeUrl = process.env.VUE_APP_STRIPE_MANAGE_PLAN_URL;

      this.planChangeDialog = true;

      window.open(stripeUrl, "_blank");
    },
    async getCompanySubscriptions() {
      const data = await serviceHandler(
        companyService.getCompanySubscriptions(),
        null,
        this.$route.name
      );

      if (data) {
        this.expirationDate = data.expires_at;
        this.subscriptionType = data.subscription_plan.name;
      }
    },

    async handleClick() {
      const data = {
        buttonText: "OK"
      };

      await usersService
        .postSubscriptionVoucher(this.accountCode)
        .then(res => {
          data.text = this.$t("message.notification.activationCodeWasAdded");
        })
        .catch(error => {
          if (error && error.response && error.response.status === 400) {
            data.text = this.$t(
              "message.notification.activationCodeWasNotAdded"
            );
          } else {
            ErrorHandler.displayError(error);
          }
        });

      await this.getCompanySubscriptions();

      this.$store.dispatch("settings/openModal", data);
      this.accountCode = "";
    },

    async pay() {
      try {
        const { data } = await PaymentsService.pay();
        if (data.redirect_to) {
          window.location.href = data.redirect_to;
        }
      } catch (error) {
        if (error.response.status === 400) {
          const data = {
            text: error.response.data.error,
            buttonText: this.$t("message.common.ok")
          };
          this.$store.dispatch("settings/openModal", data);

          return;
        }

        this.$notify({
          group: "default",
          type: "error",
          text: error.response.data.error
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.consent__description {
  font-weight: $font-weight-light;
  line-height: 20px;
  margin-bottom: 0;
}

.section-heading {
  margin-bottom: $spacing * 2;
}

.subtitle {
  @include font-size(16px);
}

.buttons {
  display: flex;
  justify-content: center;
  gap: $spacing;

  &--subscription {
    justify-content: start;
  }
}
.actions {
  display: flex;
  justify-content: flex-end;
  flex-grow: 1;
}
</style>
